import {graphql, useStaticQuery} from 'gatsby'
import React from 'react'

import {eventsRegisterctaActioned} from '@/analytics/segment'
import {EventCard} from '@/components/event-card'
import Image from '@/components/image'
import PrismicLink from '@/components/prismic-link'
import type {PleoEventsQuery, UpcomingEventsSliceFragment} from '@/graphql-types'
import {useDateFormatter} from '@/hooks/use-date-formatter'
import {useLocalizedContent} from '@/hooks/use-localized-content'
import useParams from '@/hooks/use-params'
import {CardsGridWithFilters} from '@/sections/cards-grid-with-filters'
import type {CardLayout} from '@/sections/cards-grid-with-filters'

const TITLE_PARAM = 'event-title'
const URL_FILTER_PREFIX = 'events-grid'

export const UpcomingEventsSlice = ({primary}: UpcomingEventsSliceFragment) => {
    const {allPrismicPleoEvent} = useStaticQuery<PleoEventsQuery>(query)
    const events = useLocalizedContent(allPrismicPleoEvent.nodes, {
        fallbackToDefaultLocale: false,
    }).map(({data}) => ({...data}))
    const formatDate = useDateFormatter()
    const params = useParams()

    const localizedEventType: Record<string, string> = {
        Webinar: primary.filter_event_type_webinar,
        'Live Event': primary.filter_event_type_live_event,
        Deminar: primary.filter_event_type_deminar,
    }

    const eventsData = events.map((event) => {
        const formattedDatetime = formatDate(event.datetime, {
            day: '2-digit',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit',
        })

        const datetimeArray = formattedDatetime.split(',')

        return {
            title: event.title,
            description: event.description,
            category: localizedEventType[event.event_type],
            language: event.language,
            location: event.location,
            duration: event.duration,
            date: datetimeArray[0],
            time: datetimeArray[1],
            Image: () => <Image image={event.image} />,
            CTA: (props: React.ComponentProps<typeof PrismicLink>) => (
                <PrismicLink
                    {...props}
                    {...event.link}
                    onClick={() =>
                        eventsRegisterctaActioned({
                            name: event.title,
                            location: event.location,
                            type: event.event_type,
                        })
                    }
                >
                    {primary.event_cta_text}
                </PrismicLink>
            ),
        }
    })

    return (
        <CardsGridWithFilters
            title={primary.title}
            topLink={primary.top_link}
            topLinkText={primary.top_link_text}
            cardsLayout={primary.layout as CardLayout}
            urlFilterPrefix={URL_FILTER_PREFIX}
            filters={[
                {
                    name: 'category',
                    placeholder: primary.filter_event_type_placeholder,
                    allLabel: primary.filter_event_type_all,
                },
                {
                    name: 'location',
                    placeholder: primary.filter_location_placeholder,
                    allLabel: primary.filter_location_all,
                },
            ]}
            initialFilterValues={{
                location: params.get(`${URL_FILTER_PREFIX}-location`) || '',
                category: params.get(`${URL_FILTER_PREFIX}-category`) || '',
            }}
            initialSearchTerm={params.get(TITLE_PARAM)}
            showingAllResultsLabel={primary.showing_all_results}
            showingResultsForLabel={primary.showing_all_results_for}
            clearText={primary.filter_clear}
            noResultsFoundText={primary.no_events_found}
            searchPlaceholder={primary.search_placeholder}
        >
            {eventsData.map((event, index) => (
                <EventCard key={index} {...event} />
            ))}
        </CardsGridWithFilters>
    )
}

export const upcomingEventsSliceFragment = graphql`
    fragment UpcomingEventsSlice on PrismicSlicePageDataBodyUpcomingEvents {
        __typename
        primary {
            title
            top_link_text
            top_link {
                ...PrismicLink
            }
            layout
            event_cta_text
            showing_all_results
            showing_all_results_for
            filter_event_type_placeholder
            filter_event_type_all
            filter_event_type_live_event
            filter_event_type_deminar
            filter_event_type_webinar
            filter_location_placeholder
            filter_location_all
            filter_clear
            no_events_found
            search_placeholder
        }
    }
`

export const query = graphql`
    query PleoEvents($imageBreakpoints: [Int], $threeColumns: String) {
        allPrismicPleoEvent(sort: {order: ASC, fields: data___datetime}) {
            nodes {
                _previewable
                lang
                data {
                    title
                    description
                    datetime
                    event_type
                    duration
                    language
                    location
                    link {
                        ...PrismicLink
                    }
                    image {
                        alt
                        copyright
                        url
                        dimensions {
                            height
                            width
                        }
                        gatsbyImageData(breakpoints: $imageBreakpoints, sizes: $threeColumns)
                    }
                }
            }
        }
    }
`
